import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type { MetodoPago, PagedPagos, Pago, PagoDetalle } from "./schemas";

const KEY = "pagos";

export type PagosFilters = {
  page?: number;
  size?: number;
  cliente_id?: string;
  factura_id?: string;
  contrato_id?: string;
  metodo?: MetodoPago | "";
  incluir_anulados?: boolean;
  desde?: string;
  hasta?: string;
  registrado_por?: string;
  payment_provider?: "" | "toku" | "saldo_a_favor" | "manual";  // rc52
};

function buildParams(f: PagosFilters): Record<string, string | number | boolean> {
  const p: Record<string, string | number | boolean> = {
    page: f.page ?? 1, size: f.size ?? 50,
  };
  if (f.cliente_id) p.cliente_id = f.cliente_id;
  if (f.factura_id) p.factura_id = f.factura_id;
  if (f.contrato_id) p.contrato_id = f.contrato_id;
  if (f.metodo) p.metodo = f.metodo;
  if (f.incluir_anulados) p.incluir_anulados = true;
  if (f.desde) p.desde = f.desde;
  if (f.hasta) p.hasta = f.hasta;
  if (f.registrado_por) p.registrado_por = f.registrado_por;
  if (f.payment_provider) p.payment_provider = f.payment_provider;
  return p;
}

export function usePagos(filters: PagosFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => apiCall<PagedPagos>("get", "/api/v1/pagos", { searchParams: buildParams(filters) }),
  });
}

export function usePago(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<Pago>("get", `/api/v1/pagos/${id}`),
    enabled: !!id,
  });
}

/** rc50 — Detalle extendido (cliente + factura + cajero + Toku parseado). */
export function usePagoDetalle(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detalle", id],
    queryFn: () => apiCall<PagoDetalle>("get", `/api/v1/pagos/${id}/detalle`),
    enabled: !!id,
    staleTime: 60_000, // 1 min — data es histórica, no cambia salvo anulación
  });
}

export function useAnularPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      apiCall<Pago>("post", `/api/v1/pagos/${id}/anular`, { json: { motivo } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["cobranza"] });
    },
  });
}
